import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Input, 
  IconButton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  FormControl,
  FormLabel,
  useToast
} from "@chakra-ui/react";
import { FaUser, FaCreditCard, FaTv, FaTasks, FaCog, FaCheck } from "react-icons/fa";
import { useState, useEffect } from "react";
import { useAuth } from '../context/AuthContext'; 
import { useLocation } from 'react-router-dom'; 
import { updateFirstName, updateLastName, updateEmail } from '../apis/personalInfoUpdateAPI'

const PersonalHeader = () => {
  const location = useLocation();
  const initialSection = location.state?.section || "profile";
  const [activeSection, setActiveSection] = useState(initialSection);
  const [editMode, setEditMode] = useState({
    firstName: false,
    lastName: false,
    email: false,
  });
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [isLoading, setIsLoading] = useState({
    firstName: false,
    lastName: false,
    email: false,
  });

  const toast = useToast();
  const { user } = useAuth(); // Use useAuth hook

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.user_metadata.firstname || "",
        lastName: user.user_metadata.lastname || "",
        email: user.user_metadata.email || "",
      });
    }
  }, [user]);

  const handleEditClick = async (field) => {
    // If we're already in edit mode, we need to save
    if (editMode[field]) {
      setIsLoading(prev => ({ ...prev, [field]: true }));
      
      try {
        if (field === "firstName") {
          await updateFirstName(formData.firstName);
        } else if (field === "lastName") {
          await updateLastName(formData.lastName);
        } else if (field === "email") {
          await updateEmail(formData.email);
        }
        
        // Show success toast
        toast({
          title: "Saved successfully",
          description: `Your ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} has been updated.`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        
        // Exit edit mode
        setEditMode(prev => ({ ...prev, [field]: false }));
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        
        // Show error toast
        toast({
          title: "Update failed",
          description: `There was an error updating your ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}.`,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      } finally {
        setIsLoading(prev => ({ ...prev, [field]: false }));
      }
    } else {
      // Enter edit mode
      setEditMode(prev => ({ ...prev, [field]: true }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Map section names to tab indices
  const sectionToIndex = {
    profile: 0,
    payment: 1,
    subscription: 2,
    privacy: 3,
    settings: 4
  };

  // Map tab indices to section names
  const indexToSection = ["profile", "payment", "subscription", "privacy", "settings"];

  const handleTabChange = (index) => {
    setActiveSection(indexToSection[index]);
  };

  return (
    <Box p={3} maxW="7xl" mx="auto">
      <Box 
        bg="white" 
        borderRadius="lg"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
        overflow="hidden"
        maxW={{ base: "100%", md: "700px" }}
        mx="auto"
      >
        <Tabs 
          variant="soft-rounded" 
          colorScheme="green" 
          index={sectionToIndex[activeSection]} 
          onChange={handleTabChange}
          isLazy
          size="sm"
        >
          <TabList p={3} bg="rgba(130, 202, 156, 0.05)" borderBottom="1px solid" borderColor="gray.100">
            <Tab 
              _selected={{ 
                color: "white", 
                bg: "#82ca9c",
                boxShadow: "0 2px 4px rgba(130, 202, 156, 0.3)"
              }}
              mx={1}
              py={1}
              fontSize="sm"
            >
              <HStack spacing={1}>
                <FaUser size={12} />
                <Text display={{ base: "none", md: "block" }} fontSize="xs">Profile</Text>
              </HStack>
            </Tab>
            <Tab 
              _selected={{ 
                color: "white", 
                bg: "#82ca9c",
                boxShadow: "0 2px 4px rgba(130, 202, 156, 0.3)"
              }}
              mx={1}
              py={1}
              fontSize="sm"
            >
              <HStack spacing={1}>
                <FaCreditCard size={12} />
                <Text display={{ base: "none", md: "block" }} fontSize="xs">Payment</Text>
              </HStack>
            </Tab>
            <Tab 
              _selected={{ 
                color: "white", 
                bg: "#82ca9c",
                boxShadow: "0 2px 4px rgba(130, 202, 156, 0.3)"
              }}
              mx={1}
              py={1}
              fontSize="sm"
            >
              <HStack spacing={1}>
                <FaTv size={12} />
                <Text display={{ base: "none", md: "block" }} fontSize="xs">Subscription</Text>
              </HStack>
            </Tab>
            <Tab 
              _selected={{ 
                color: "white", 
                bg: "#82ca9c",
                boxShadow: "0 2px 4px rgba(130, 202, 156, 0.3)"
              }}
              mx={1}
              py={1}
              fontSize="sm"
            >
              <HStack spacing={1}>
                <FaTasks size={12} />
                <Text display={{ base: "none", md: "block" }} fontSize="xs">Privacy</Text>
              </HStack>
            </Tab>
            <Tab 
              _selected={{ 
                color: "white", 
                bg: "#82ca9c",
                boxShadow: "0 2px 4px rgba(130, 202, 156, 0.3)"
              }}
              mx={1}
              py={1}
              fontSize="sm"
            >
              <HStack spacing={1}>
                <FaCog size={12} />
                <Text display={{ base: "none", md: "block" }} fontSize="xs">Settings</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel p={4}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={3}>Personal Information</Text>
                
                <VStack spacing={4} align="stretch">
                  <FormControl size="sm">
                    <FormLabel fontWeight="medium" color="gray.700" fontSize="xs" mb={1}>First Name</FormLabel>
                    <Flex>
                      <Input
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        isDisabled={!editMode.firstName || isLoading.firstName}
                        borderRadius="md"
                        borderColor={editMode.firstName ? "#82ca9c" : "gray.200"}
                        _hover={{ borderColor: editMode.firstName ? "#82ca9c" : "gray.300" }}
                        _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                        mr={2}
                        size="sm"
                        h="32px"
                      />
                      <Button 
                        onClick={() => handleEditClick("firstName")}
                        isLoading={isLoading.firstName}
                        loadingText="Saving"
                        bg={editMode.firstName ? "#82ca9c" : "white"}
                        color={editMode.firstName ? "white" : "gray.700"}
                        border="1px solid"
                        borderColor={editMode.firstName ? "#82ca9c" : "gray.200"}
                        _hover={{
                          bg: editMode.firstName ? "#6db389" : "gray.50",
                          borderColor: editMode.firstName ? "#6db389" : "gray.300"
                        }}
                        minW="70px"
                        h="32px"
                        size="sm"
                        fontSize="xs"
                      >
                        {editMode.firstName ? "Save" : "Edit"}
                      </Button>
                    </Flex>
                  </FormControl>
                  
                  <FormControl size="sm">
                    <FormLabel fontWeight="medium" color="gray.700" fontSize="xs" mb={1}>Last Name</FormLabel>
                    <Flex>
                      <Input
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        isDisabled={!editMode.lastName || isLoading.lastName}
                        borderRadius="md"
                        borderColor={editMode.lastName ? "#82ca9c" : "gray.200"}
                        _hover={{ borderColor: editMode.lastName ? "#82ca9c" : "gray.300" }}
                        _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                        mr={2}
                        size="sm"
                        h="32px"
                      />
                      <Button 
                        onClick={() => handleEditClick("lastName")}
                        isLoading={isLoading.lastName}
                        loadingText="Saving"
                        bg={editMode.lastName ? "#82ca9c" : "white"}
                        color={editMode.lastName ? "white" : "gray.700"}
                        border="1px solid"
                        borderColor={editMode.lastName ? "#82ca9c" : "gray.200"}
                        _hover={{
                          bg: editMode.lastName ? "#6db389" : "gray.50",
                          borderColor: editMode.lastName ? "#6db389" : "gray.300"
                        }}
                        minW="70px"
                        h="32px"
                        size="sm"
                        fontSize="xs"
                      >
                        {editMode.lastName ? "Save" : "Edit"}
                      </Button>
                    </Flex>
                  </FormControl>
                  
                  <FormControl size="sm">
                    <FormLabel fontWeight="medium" color="gray.700" fontSize="xs" mb={1}>Email Address</FormLabel>
                    <Flex>
                      <Input
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        isDisabled={!editMode.email || isLoading.email}
                        borderRadius="md"
                        borderColor={editMode.email ? "#82ca9c" : "gray.200"}
                        _hover={{ borderColor: editMode.email ? "#82ca9c" : "gray.300" }}
                        _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                        mr={2}
                        size="sm"
                        h="32px"
                      />
                      <Button 
                        onClick={() => handleEditClick("email")}
                        isLoading={isLoading.email}
                        loadingText="Saving"
                        bg={editMode.email ? "#82ca9c" : "white"}
                        color={editMode.email ? "white" : "gray.700"}
                        border="1px solid"
                        borderColor={editMode.email ? "#82ca9c" : "gray.200"}
                        _hover={{
                          bg: editMode.email ? "#6db389" : "gray.50",
                          borderColor: editMode.email ? "#6db389" : "gray.300"
                        }}
                        minW="70px"
                        h="32px"
                        size="sm"
                        fontSize="xs"
                      >
                        {editMode.email ? "Save" : "Edit"}
                      </Button>
                    </Flex>
                  </FormControl>
                </VStack>
              </Box>
            </TabPanel>
            
            <TabPanel p={4}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={3}>Payment Information</Text>
                <Text color="gray.600" fontSize="sm">Your payment information will appear here.</Text>
              </Box>
            </TabPanel>
            
            <TabPanel p={4}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={3}>Your Subscription</Text>
                <Text color="gray.600" fontSize="sm">Your subscription details will appear here.</Text>
              </Box>
            </TabPanel>
            
            <TabPanel p={4}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={3}>Privacy Settings</Text>
                <Text color="gray.600" fontSize="sm">Your privacy settings will appear here.</Text>
              </Box>
            </TabPanel>
            
            <TabPanel p={4}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={3}>Account Settings</Text>
                <Text color="gray.600" fontSize="sm">Your account settings will appear here.</Text>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
};

export default PersonalHeader;